import { forwardRef } from 'react';
import { cn } from '../../lib/utils';

/**
 * Card component - Lucky Number Design System
 *
 * Features:
 * - Cream background (for dark backgrounds) or dark variant
 * - Ticket variant with perforated edge effect
 * - Hover effects with lift and shadow
 * - Interactive/clickable variant
 */

const paddings = {
  none: 'p-0',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

const Card = forwardRef(function Card(
  {
    children,
    hover = false,
    interactive = false,
    padding = 'md',
    variant = 'default', // 'default' | 'dark' | 'ticket'
    className,
    onClick,
    as: Component = 'div',
    ...props
  },
  ref
) {
  const isClickable = interactive || onClick;

  const variants = {
    default: 'bg-cream text-midnight border border-cream-dark/30',
    dark: 'bg-midnight-light text-cream border border-midnight-lighter',
    ticket: 'bg-cream text-midnight ticket-edge',
  };

  return (
    <Component
      ref={ref}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.(e);
              }
            }
          : undefined
      }
      className={cn(
        // Base styles
        'rounded-lg',
        'shadow-card',
        // Variant styles
        variants[variant],
        // Transition
        'transition-all duration-300 ease-out',
        // Hover effect
        (hover || isClickable) && [
          'hover:shadow-card-hover hover:-translate-y-1',
        ],
        // Interactive/clickable styles
        isClickable && [
          'cursor-pointer',
          'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-midnight',
          'active:scale-[0.99]',
        ],
        // Padding
        paddings[padding],
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
});

export default Card;

/**
 * Card Header - for title section
 */
export function CardHeader({ children, className, ...props }) {
  return (
    <div
      className={cn(
        'px-4 py-3 border-b border-cream-dark/30',
        '-mx-4 -mt-4 mb-4',
        'first:rounded-t-lg',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Card Title - heading inside card header
 */
export function CardTitle({ children, className, as: Component = 'h3', ...props }) {
  return (
    <Component
      className={cn(
        'font-display font-normal text-xl text-midnight',
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
}

/**
 * Card Description - subtitle/description text
 */
export function CardDescription({ children, className, ...props }) {
  return (
    <p
      className={cn('text-sm text-midnight/60 mt-1', className)}
      {...props}
    >
      {children}
    </p>
  );
}

/**
 * Card Body - main content area
 */
export function CardBody({ children, className, ...props }) {
  return (
    <div className={cn(className)} {...props}>
      {children}
    </div>
  );
}

/**
 * Card Footer - for actions, typically at bottom
 */
export function CardFooter({ children, className, ...props }) {
  return (
    <div
      className={cn(
        'px-4 py-3 border-t border-cream-dark/30',
        '-mx-4 -mb-4 mt-4',
        'last:rounded-b-lg',
        'flex items-center gap-3',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Card Image - for cards with hero image
 */
export function CardImage({
  src,
  alt,
  aspectRatio = 'raffle-card',
  className,
  overlay,
  ...props
}) {
  const aspectRatios = {
    'raffle-card': 'aspect-[4/3]',
    'raffle-hero': 'aspect-[16/9]',
    square: 'aspect-square',
    video: 'aspect-video',
  };

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-t-lg',
        '-mx-4 -mt-4 mb-4',
        aspectRatios[aspectRatio],
        className
      )}
      {...props}
    >
      <img
        src={src}
        alt={alt}
        className={cn(
          'w-full h-full object-cover',
          'transition-transform duration-300',
          'group-hover:scale-105'
        )}
      />
      {overlay && (
        <div className="absolute inset-0 bg-gradient-to-t from-midnight/60 to-transparent">
          {overlay}
        </div>
      )}
    </div>
  );
}

/**
 * Skeleton Card - loading placeholder
 */
export function CardSkeleton({ className, showImage = true, lines = 2, dark = false, ...props }) {
  return (
    <Card variant={dark ? 'dark' : 'default'} className={cn('animate-pulse', className)} {...props}>
      {showImage && (
        <div className={cn(
          'aspect-[4/3] rounded-lg -mx-4 -mt-4 mb-4',
          dark ? 'bg-midnight-lighter' : 'bg-cream-dark'
        )} />
      )}
      <div className="space-y-3">
        <div className={cn('h-4 rounded w-3/4', dark ? 'bg-midnight-lighter' : 'bg-cream-dark')} />
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className={cn(
              'h-3 rounded',
              dark ? 'bg-midnight-lighter' : 'bg-cream-dark',
              i === lines - 1 && 'w-1/2'
            )}
          />
        ))}
      </div>
    </Card>
  );
}

/**
 * Featured Card - highlighted card with accent glow effect
 */
export function FeaturedCard({ children, className, ...props }) {
  return (
    <Card
      className={cn(
        'border-accent/30 shadow-glow-accent',
        'hover:shadow-glow-accent hover:border-accent/50',
        className
      )}
      hover
      {...props}
    >
      {children}
    </Card>
  );
}

/**
 * Stat Card - for displaying metrics
 */
export function StatCard({
  label,
  value,
  icon,
  trend,
  trendDirection,
  variant = 'default',
  className,
  ...props
}) {
  const isDark = variant === 'dark';

  return (
    <Card variant={variant} className={cn('flex items-center gap-4', className)} {...props}>
      {icon && (
        <div className={cn(
          'w-12 h-12 rounded-lg flex items-center justify-center shrink-0',
          isDark ? 'bg-petrol/30 text-accent' : 'bg-petrol/10 text-petrol'
        )}>
          {icon}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className={cn(
          'text-xs font-bold uppercase tracking-wider truncate',
          isDark ? 'text-cream/60' : 'text-midnight/60'
        )}>
          {label}
        </p>
        <p className={cn(
          'text-2xl font-display font-normal',
          isDark ? 'text-cream' : 'text-midnight'
        )}>
          {value}
        </p>
        {trend && (
          <p
            className={cn(
              'text-sm font-medium',
              trendDirection === 'up' && 'text-success',
              trendDirection === 'down' && 'text-error'
            )}
          >
            {trendDirection === 'up' && '↑'}
            {trendDirection === 'down' && '↓'}
            {' '}{trend}
          </p>
        )}
      </div>
    </Card>
  );
}

/**
 * Ticket Card - styled like a raffle ticket with perforated edge
 */
export function TicketCard({ children, className, ...props }) {
  return (
    <div
      className={cn(
        'relative bg-cream text-midnight rounded-lg overflow-hidden',
        'shadow-ticket',
        className
      )}
      {...props}
    >
      {/* Perforated left edge */}
      <div className="absolute left-0 top-0 bottom-0 w-4 bg-midnight z-10">
        <div className="h-full flex flex-col justify-around py-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="w-4 h-4 rounded-full bg-cream -ml-2" />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="pl-6">
        {children}
      </div>
    </div>
  );
}
